import year from '@/api/academic-year'
import branch from '@/api/branch'
import {
  default as classOp,
  default as classOperation
} from '@/api/class-operation'
import FeeType from '@/api/fee-type'
import invoice from '@/api/invoice'
import mainProgram from '@/api/main-program'
import payment from '@/api/report-payment'
import StudentProfile from '@/api/student-profile'
import subProgram from '@/api/sub-program'
import teacher from '@/api/teacher-profile'
import term from '@/api/term-semester'
import { camelCase, mapKeys } from 'lodash'

import { baseMutations, baseState } from '@/store/mixins'
import moment from 'moment'

class Pagination {
  constructor(paging = {}) {
    this.currentPage = paging.currentPage ? parseInt(paging.currentPage) : 1
    this.lastPage = paging.lastPage ? parseInt(paging.lastPage) : 0
    this.perPage = paging.perPage ? parseInt(paging.perPage) : 15
    this.total = paging.total ? parseInt(paging.total) : 0
    this.from = this.perPage * (this.currentPage - 1) + 1
    this.to = this.perPage * this.currentPage
  }
}

export const state = {
  ...baseState,
  studentStatus: [],
  date_from: moment().format('DD-MM-YYYY'),
  date_to: moment().format('DD-MM-YYYY'),
  is_deleted: 1
}

export const getters = {}

export const mutations = {
  ...baseMutations,
  SET_STATUS(state, data) {
    state.studentStatus = data
  },
  RECEIVE_RESOURCES(state, records) {
    const { data, ...meta } = records
    state.resources = data
    state.pagination = new Pagination(mapKeys(meta, (v, k) => camelCase(k)))
  },
  SET_DATE_FROM(state, date) {
    state.date_from = date
  },
  SET_DATE_TO(state, date) {
    state.date_to = date
  }
}

export const actions = {
  getBranch(context, attributes = {}) {
    return branch.get({
      params: {
        fields: {
          setup_branch_profiles: 'branch_id,branch_name_en'
        },
        filter: {
          user_branches: true
        },
        page: -1
      },
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getMainProgram(context, attributes = {}) {
    return mainProgram.get({
      params: {
        fields: {
          academic_program_mains: 'acp_id,acp_name_foreign'
        },
        page: -1
      },
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getSubProgram(context, attributes = {}) {
    return subProgram.get({
      params: Object.assign(
        {
          fields: {
            academic_program_subs: 'acps_id,acps_name_foreign'
          },
          page: -1
        },
        attributes
      ),
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getTerm(context, attributes = {}) {
    return term.get({
      params: Object.assign(
        {
          fields: {
            academic_terms: 'ac_term_id,term_name,ac_status_id'
          },
          page: -1
        },
        attributes
      ),
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getTeacher(context, acp_id) {
    return classOperation.get({
      params: {
        include: 'teacher,academic_study_time',
        filter: {
          acp_id: acp_id,
          ac_status_id: 1
        },
        page: -1
      },
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getTeacherCurrentBranch(context, attributes = {}) {
    return teacher.get({
      params: Object.assign(
        {},
        {
          fields: {
            setup_teacher_profiles: 'teacher_id,fname_foreign,lname_foreign'
          },
          filter: {
            current_branch: true
          },
          page: -1
        },
        attributes
      )
    })
  },
  getYear(context, attributes = {}) {
    return year.get({
      params: Object.assign(
        {
          fields: {
            academic_years: 'ac_year_id,ac_year,ac_status_id'
          },
          page: -1
        },
        attributes
      ),
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getClass(context, attributes = {}) {
    return classOp.get({
      params: Object.assign(
        {
          include: 'teacher,academic_study_time',
          fields: {
            academic_classes: 'class_id,class_name,teacher_resp_id,ac_st_id',
            teacher: 'teacher_id,fname_foreign,lname_foreign',
            academic_study_time: 'ac_st_id,study_time'
          },
          page: -1
        },
        attributes
      ),
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  fetchFeeType(context, attributes = {}) {
    return FeeType.get({
      params: Object.assign(
        {
          fields: {
            setup_payment_fee_types: 'fee_type_id,fee_type_name'
          },
          page: -1
        },
        attributes
      ),
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  getInvoice(context, attributes = {}) {
    return invoice.get({
      params: {
        fields: {
          student_invoice_mains: 'inv_id,inv_number'
        },
        filter: {
          is_deleted: 1
        },
        branch_id: attributes.branch_id,
        page: -1
      },
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  fetchFeeSetup(context, attributes = {}) {
    return payment.feeSetup({ params: attributes })
  },
  async getStudentStatus(context) {
    if (!context.state.studentStatus.length > 0) {
      let response = await StudentProfile.getStatus()
      context.commit('SET_STATUS', response.data)
    }
  },
  //get class operation by teacher id and filter from schedule
  getClassSchedule(context, teacher_id) {
    return payment.getClassSchedule(teacher_id)
  },
  // query cashier list for cash collection by cashier
  getCashier(context) {
    return payment.getCashier()
  },
  getPromoType(context) {
    return payment.getPromoType()
  },
  // payment detail
  paymentDetail(context, data) {
    return payment.paymentDetail(data)
  },
  paymentDetailExcel(context, data) {
    return payment.paymentDetailExcel(data)
  },
  // summary cash collecion
  summary(context, data) {
    return payment.summary(data)
  },
  summaryExcel(context, data) {
    return payment.summaryExcel(data)
  },
  // cash collection by cashier
  bycashier(context, data) {
    return payment.bycashier(data)
  },
  // payment by service
  byservice(context, data) {
    return payment.byservice(data)
  },
  // monthly payment
  monthlyPayment(context, data) {
    return payment.monthlypayment(data)
  },
  // monthly payment by fee
  monthlyPaymentByFee(context, data) {
    return payment.monthlypaymentbyfee(data)
  },
  // student unpaid
  fetchStudentUnpaid(context, attrubutes = {}) {
    let params = Object.assign(
      {
        page: context.state.pagination.page,
        perPage: context.state.pagination.perPage
      },
      attrubutes
    )
    return payment.getStudentUnpaid({ params }).then(response => {
      context.commit('RECEIVE_RESOURCES', response.data)
      return Promise.resolve(response.da)
    })
  },
  studentUnpaid(context, data) {
    return payment.studentUnpaid(data)
  },
  // void invoice
  voidInvoice(context, data) {
    return payment.voidInvoice(data)
  },
  // void invoice
  annualPayment(context, data) {
    return payment.annualPayment(data)
  },
  // overdue payment
  overduePayment(context, data) {
    return payment.overduePayment(data)
  },
  // student payment
  studentPayment(context, data) {
    return payment.studentPayment(
      Object.assign(
        {
          date_from: context.state.date_from,
          date_to: context.state.date_to
        },
        data
      )
    )
  },
  studentPaymentExcel(context, data) {
    return payment.studentPaymentExcel(
      Object.assign(
        {
          date_from: context.state.date_from,
          date_to: context.state.date_to
        },
        data
      )
    )
  },
  studentPaymentFormViewModel(context, payload = {}) {
    return payment.studentPaymentFormViewModel({
      params: payload,
      headers: {
        'X-PERMISSION-ALLOW-CODE': context.rootGetters.permissionAllowCode
      }
    })
  },
  // discount payment
  discountPayment(context, data) {
    return payment.discountPayment(
      Object.assign(
        {
          date_from: context.state.date_from,
          date_to: context.state.date_to
        },
        data
      )
    )
  },
  // discount payment
  yearlyPaymentList(context, data) {
    return payment.yearlyPaymentList(data)
  },
  // export excel
  exportYearlyPaymentList(context, data) {
    return payment.yearlyPaymentListExcel(data)
  },
  // sale day book
  saleDayBook(context, data) {
    return payment.saleDayBook(data)
  },
  saleDayBookExcel(context, data) {
    return payment.saleDayBookExcel(data)
  },
  // payment tracking
  paymentTracking(context, data) {
    return payment.paymentTracking(data)
  },
  getFormViewData(context, payload) {
    return payment.formViewModel(payload)
  },
  byserviceExcel(context, data) {
    return payment.byserviceExcel(data)
  }
}
